// The supplier's bill (PDF or picture) on a Flow bill (Stephanie 2026-10-22:
// "attach supplier PDF to a bill"). Uploads through /files/presign (internal,
// purpose vendor_bill) and hands back the file; the caller sends its id with
// the bill, or (on the bill page) saves it straight away.
import { useState } from "react";
import { Paperclip } from "lucide-react";
import { api } from "@/lib/api";

export interface AttachedFile {
  fileId: string;
  filename: string;
  url?: string | null;
}

export const SUPPLIER_BILL_ACCEPT = "application/pdf,image/png,image/jpeg,image/webp,image/heic";

export async function uploadSupplierBill(file: File, prefix: string): Promise<AttachedFile> {
  const presign = await api<{ file_id: string; upload_url: string; live: boolean }>("/files/presign", {
    method: "POST",
    body: JSON.stringify({
      filename: file.name,
      mime_type: file.type || "application/octet-stream",
      size_bytes: file.size,
      file_purpose: "vendor_bill",
      visibility: "internal",
      prefix,
    }),
  });
  if (presign.live) {
    const put = await fetch(presign.upload_url, { method: "PUT", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file });
    if (!put.ok) throw new Error("upload failed");
  }
  return { fileId: presign.file_id, filename: file.name };
}

export function SupplierBillFile(props: {
  value: AttachedFile | null;
  onChange: (f: AttachedFile | null) => void;
  prefix: string;
  disabled?: boolean;
  onBusy?: (busy: boolean) => void;
}) {
  const { value, onChange, prefix, disabled } = props;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pick = async (file: File) => {
    if (!/^(application\/pdf|image\/)/.test(file.type)) {
      setError("Attach a PDF or a picture of the supplier's bill.");
      return;
    }
    setBusy(true); props.onBusy?.(true); setError(null);
    try {
      onChange(await uploadSupplierBill(file, prefix));
    } catch {
      setError("Couldn't upload that file. Try again.");
    } finally {
      setBusy(false); props.onBusy?.(false);
    }
  };
  return (
    <div className="text-sm">
      {value ? (
        <p className="flex flex-wrap items-center gap-2 text-ink">
          <Paperclip className="h-4 w-4 text-muted" aria-hidden />
          <span className="font-medium">{value.filename}</span>
          {value.url ? <a href={value.url} target="_blank" rel="noreferrer" className="text-xs font-semibold underline">View</a> : null}
          {!disabled ? <button type="button" className="text-xs font-semibold text-risk underline" onClick={() => onChange(null)}>Remove</button> : null}
        </p>
      ) : (
        <label className={`inline-flex items-center gap-2 text-sm font-semibold text-ink ${disabled ? "opacity-50" : "cursor-pointer underline"}`}>
          <Paperclip className="h-4 w-4" aria-hidden />
          {busy ? "Uploading…" : "Attach the supplier's bill (PDF or picture)"}
          <input
            type="file"
            accept={SUPPLIER_BILL_ACCEPT}
            className="sr-only"
            disabled={busy || disabled}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void pick(f); }}
          />
        </label>
      )}
      {error ? <p className="mt-1 text-xs font-semibold text-risk">{error}</p> : null}
    </div>
  );
}
